import { useEffect, useState } from 'react';

interface UseServerTimeOptions {
  /**
   * Allows correcting to the client clock right after mount, before the server
   * time arrives. Useful for display-only values in a long-open session (the
   * client clock is a better approximation than a placeholder frozen at page
   * load), but **not** where the time has to be tamper-proof — it can simply be
   * changed.
   *
   * Nothing uses the option at the moment: the calendar needs the tamper
   * protection, and the home page has formatted its date on the server since
   * the switch to `force-dynamic` (`todayLabel`).
   */
  isClientClockFallbackEnabled?: boolean;
}

/**
 * Current time as the basis for time-dependent UI in client components.
 *
 * Background: `/kalender` renders per request (`force-dynamic`), so the
 * server-generated placeholder is current at render time. In the client it
 * freezes afterwards — a long-open session would otherwise only update its date
 * boundaries on the next full reload and keep computing with yesterday across
 * midnight. On top of that comes the tamper protection: the boundaries must not
 * be shiftable via the client system clock. The hook therefore delivers the
 * time in stages:
 *
 * 1. at render: `initialTime` — the server-generated placeholder
 * 2. optionally after mount: the client clock (see `isClientClockFallbackEnabled`)
 * 3. as soon as the call returns: the server time from `/api/server-time`
 *    (force-dynamic, no-store), independent of the client system clock
 *
 * Important: the placeholder has to come from the server as a prop and must not
 * be a `new Date()` in the client. Otherwise the hydration render deviates from
 * the server HTML, React adopts that deviation into its virtual DOM and leaves
 * the already-correct text untouched afterwards — the update from step 2/3
 * would never reach the DOM.
 *
 * @param initialTime ISO string, generated on the server (e.g. `new Date().toISOString()`)
 */
export const useServerTime = (
  initialTime: string,
  { isClientClockFallbackEnabled = false }: UseServerTimeOptions = {},
) => {
  const [now, setNow] = useState(() => {
    const initial = new Date(initialTime);
    // Without the guard an Invalid Date would break the time-dependent UI
    // (`format()` throws, `isSameDay`/`addDays` return NaN).
    return Number.isNaN(initial.getTime()) ? new Date() : initial;
  });

  useEffect(() => {
    if (isClientClockFallbackEnabled) {
      // The placeholder froze when the page was loaded and can be stale in a
      // long-open session. The client clock is the better approximation until
      // the server time arrives.
      setNow(new Date());
    }

    const controller = new AbortController();

    fetch('/api/server-time', { signal: controller.signal })
      .then((res) => {
        // The status check is required because the gateway serves errors as
        // valid JSON ({"message": …}, e.g. a 401 on expired basic auth).
        // Without it res.json() would succeed and the failure would vanish
        // silently in the serverTime guard below.
        if (!res.ok) {
          throw new Error(`/api/server-time responded ${res.status}`);
        }

        return res.json();
      })
      .then((data: { serverTime?: string }) => {
        const fresh = data.serverTime ? new Date(data.serverTime) : null;
        // Never let an invalid or missing value reach the state – an Invalid
        // Date would break the time-dependent UI (`format()` throws,
        // `isSameDay`/`addDays` return NaN).
        if (!fresh || Number.isNaN(fresh.getTime())) {
          throw new Error('/api/server-time returned no usable serverTime');
        }

        setNow(fresh);
      })
      .catch((error: unknown) => {
        // An abort caused by unmount is not a failure.
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }

        // The last value set (placeholder or client clock) stays in place. The
        // log is the only hint: without the client-clock fallback the calendar
        // keeps computing on a possibly day-old time base without that showing
        // up anywhere in the UI.
        console.warn('[useServerTime] Serverzeit nicht verfügbar:', error);
      });

    return () => controller.abort();
  }, [isClientClockFallbackEnabled]);

  return now;
};
